import { useEffect, useContext, useState, useCallback, useMemo } from 'react';
import CommonLayout from './common-layout';
import { IAddress } from 'helpers/types';
import DateSelector from 'components/common/date-selector';
import '../../styles/add-edit-workshop-appoitnment.scss';
import InputField from 'components/common/input-field';
import Textarea from 'components/common/textarea-field';
import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import {
  createWorkshopAppointment,
  editWorkshopAppointment,
  IAddEditWorkshopAppointmentRequest,
  IMedia,
  IWorkshopAppointmentCustomerData,
  uploadWorkshopMedia,
  getVehiclesOptions,
} from 'store/features/workshopAppointmentsSlice';
import { AppContext } from 'context/app-context';
import Select, { IDefaultOption } from 'components/common/select';
import { usersRoles, initSideModal } from 'helpers/utils';
import {
  getAvailableBookingSlotsAction,
  clearAvailableBookingSlots,
  ISlot,
  getOrgParkings,
  IParkingOption,
} from 'store/features/slotsSlice';
import { getOrganizationOptions } from 'store/features/organizationSlice';
import moment from 'moment';
import CloudUpload from 'components/common/cloud-upload';
import { ReactComponent as CancelIcon } from 'assets/icon/cancel.svg';
import { ReactComponent as PlayIcon } from 'assets/icon/play.svg';
import { setPreviewFileData } from 'store/features/modelSlice';
import { ReactComponent as SearchIcon } from 'assets/icon/search.svg';
import toast from 'react-hot-toast';
import { IUploadImageRequestParam } from 'store/features/organizationSlice';
import { setSideModalData } from 'store/features/modelSlice';
import CheckBox from 'components/common/checkbox';
import {
  IWorkshopAppointmentAddEditData,
  IWorkshopAppointmentVehicleOption,
} from 'interfaces/workshop-appointment-interface';
import { getVehicleAlerts } from 'store/features/vehicleSlice';
import { IVehicleAlertItem } from 'interfaces/vehicle-interface';
import { useTranslation } from 'react-i18next';

export interface IAddEditOtherStation extends IAddress {
  isEdited?: boolean;
  index: number;
}
interface IError {
  vehicle_id?: string;
  other_service_type?: string;
  service_description?: string;
}

export interface IUploadMediaResponse {
  key: string;
  url: string;
  download_url: string;
}

const initWorkShopAppointmentDetails: IWorkshopAppointmentAddEditData = {
  service_description: '',
  other_service_type: '',
  special_note: '',
  user_id: '',
  user_details: null,
  vehicle_id: '',
  appointment_date: moment().toString(),
  time: '',
  replace_car: false,
  alert_id: '',
};

const AddEditWorkshopAppointment = () => {
  const { setLoader } = useContext(AppContext);
  const dispatch = useAppDispatch();
  const { workshopAppointmentDetails, vehicles } = useAppSelector(
    state => state.workShopAppointment,
  );
  const { selectedOrganization, user } = useAppSelector(state => state.auth);
  const { users } = useAppSelector(state => state.user);
  const { selectedVehicleALerts } = useAppSelector(state => state.vehicles);
  const { t } = useTranslation();
  const {
    sideModal: {
      content: { callBackFun, formFor },
    },
  } = useAppSelector(state => state.modal);
  const [dropdownSearch, setDropdownSearch] = useState<string>('');
  const [alertDropdownSearch, setAlertDropdownSearch] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<IError>({});
  const [workShopAppointment, setWorkshopAppointment] = useState<IWorkshopAppointmentAddEditData>(
    initWorkShopAppointmentDetails,
  );
  const [selectedVehicle, setSelectedVehicle] = useState<IWorkshopAppointmentVehicleOption | null>(
    null,
  );
  const [vehiclesOptions, setVehiclesOptions] = useState<IDefaultOption[]>([]);
  const [alertsList, setAlertList] = useState<IDefaultOption[]>([]);
  const [selectedSlotId, setSelectedSlotId] = useState<string>('');
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(formFor === 'EDIT');
  const availableBookingSlots = useAppSelector(state => state.slots.availableBookingSlots);

  // Org & parking selection states
  const [orgOptions, setOrgOptions] = useState<IDefaultOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<IDefaultOption | null>(null);
  const [parkingOptions, setParkingOptions] = useState<IDefaultOption[]>([]);
  const [selectedParking, setSelectedParking] = useState<IDefaultOption | null>(null);

  const isSuperAdmin = selectedOrganization?.role_name === usersRoles?.SUPER_ADMIN;
  const isDealerAdmin = selectedOrganization?.role_name === usersRoles?.DEALER_ADMIN;
  const hasMultipleOrgs = (user?.login_details?.length || 0) > 1;
  const showOrgDropdown = isSuperAdmin || (isDealerAdmin && hasMultipleOrgs);

  // Fetch slots from API for selected organization + selected date
  const fetchAvailableSlots = useCallback(
    async (orgId: string | undefined, appointmentDate: string, parkingId?: string) => {
      if (!orgId) {
        dispatch(clearAvailableBookingSlots());
        return;
      }
      const day = moment(appointmentDate).format('DD/MM/YYYY');
      try {
        await dispatch(getAvailableBookingSlotsAction({
          organization_id: orgId,
          day,
          parking_id: parkingId
        }));
      } catch {
        // handled by interceptor
      }
    },
    [dispatch],
  );

  const fetchVehiclesList = async (orgId?: string) => {
    let params: any = {};
    if (orgId) {
      params.organization_id = orgId;
    } else if (selectedOrganization?.role_name === usersRoles.DEALER_ADMIN) {
      params = {
        organization_id: selectedOrganization?.organization_id,
      };
    }
    const response = await dispatch(
      getVehiclesOptions({ params: { ...params, is_from_book_workshop: true } }),
    )
      .then(() => setLoader(false))
      .catch(() => setLoader(false));
    return response;
  };
  const setWorkshopAppointmentData = () => {
    try {
      if (workshopAppointmentDetails) {
        const updatedWorkshopAppointmentData: IWorkshopAppointmentAddEditData = {
          _id: workshopAppointmentDetails?._id || '',
          alert_id:
            (workshopAppointmentDetails?.other_service_type
              ? 'OTHER'
              : workshopAppointmentDetails?.alert_details?._id) || '',
          other_service_type: workshopAppointmentDetails?.other_service_type || '',
          service_description: workshopAppointmentDetails?.service_description || '',
          special_note: workshopAppointmentDetails?.special_note || '',
          user_id: workshopAppointmentDetails?.user_id || '',
          user_details: workshopAppointmentDetails?.user_details || null,
          vehicle_id: workshopAppointmentDetails?.vehicle_id || '',
          appointment_date: workshopAppointmentDetails?.slot?.date || '',
          time: workshopAppointmentDetails?.slot?.start_time || '',
          media: workshopAppointmentDetails?.media,
          replace_car: workshopAppointmentDetails?.replace_car,
        };
        setSelectedSlotId(workshopAppointmentDetails?.slot_id || '');
        setWorkshopAppointment(updatedWorkshopAppointmentData);
      }
    } catch (error) {
      setLoader(false);
    }
  };

  const fetchData = async () => {
    try {
      const orgId = selectedOrg?.id || selectedOrg?._id || selectedOrganization?.organization_id;
      await fetchVehiclesList(orgId);
    } catch (error) {
      setLoader(false);
    }
  };

  // Fetch organisation options
  useEffect(() => {
    if (showOrgDropdown) {
      dispatch(getOrganizationOptions({ request: {} }))
        .then((data: any) => {
          const opts = (data || []).map((org: any) => ({
            id: org._id || org.id,
            _id: org._id || org.id,
            name: org.organization_name || org.name,
          }));
          setOrgOptions(opts);

          if (formFor === 'EDIT' && workshopAppointmentDetails?.slot?.organization_id) {
            const found = opts.find(
              (o: IDefaultOption) => o.id === workshopAppointmentDetails.slot?.organization_id || o._id === workshopAppointmentDetails.slot?.organization_id
            );
            if (found) setSelectedOrg(found);
          } else if (opts.length > 0) {
            setSelectedOrg(opts[0]);
          }
        })
        .catch(() => { });
    } else {
      // Single org — use the org from auth
      if (selectedOrganization?.organization_id) {
        const singleOrg: IDefaultOption = {
          id: selectedOrganization.organization_id,
          _id: selectedOrganization.organization_id,
          name: selectedOrganization.organization_name || '',
        };
        setSelectedOrg(singleOrg);
        setOrgOptions([singleOrg]);
      }
    }
  }, [showOrgDropdown]);

  // Fetch parkings when org changes, and re-fetch vehicles
  useEffect(() => {
    const orgId = selectedOrg?.id || selectedOrg?._id;
    if (!orgId) {
      setParkingOptions([]);
      setSelectedParking(null);
      return;
    }

    // Clear vehicle and alerts on organization change to avoid invalid associations
    if (formFor !== 'EDIT') {
      setSelectedVehicle(null);
      setAlertList([]);
      setWorkshopAppointment(prev => ({ ...prev, vehicle_id: '', alert_id: '', service_description: '' }));
    }

    // Fetch parkings
    dispatch(getOrgParkings({ org_id: orgId }))
      .then((parkings: IParkingOption[]) => {
        const opts = parkings.map(p => ({
          id: p._id,
          _id: p._id,
          name: p.address_title || p.city || 'Parking',
        }));
        setParkingOptions(opts);

        if (formFor === 'EDIT' && workshopAppointmentDetails?.slot?.parking_id) {
          const found = opts.find(o => o.id === workshopAppointmentDetails.slot?.parking_id);
          if (found) setSelectedParking(found);
        } else if (opts.length === 1) {
          setSelectedParking(opts[0]);
        } else {
          setSelectedParking(null);
        }
      })
      .catch(() => {
        setParkingOptions([]);
        setSelectedParking(null);
      });

    // Re-fetch vehicles
    fetchVehiclesList(orgId);
  }, [selectedOrg]);

  useEffect(() => {
    try {
      setLoader(true);
      if (formFor === 'EDIT') {
        setWorkshopAppointmentData();
      }
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    try {
      setLoader(true);
      const updatedOptions: IDefaultOption[] = (vehicles || []).map(data => ({
        id: data?._id || '',
        name: `${data?.model} (${data?.plate})` || '',
        value: data?.plate || '',
      }));
      if (workShopAppointment?.vehicle_id && !selectedVehicle) {
        const vehicleSel =
          vehicles.find(data => data?._id === workShopAppointment?.vehicle_id) || null;
        setSelectedVehicle(vehicleSel);
      }
      setVehiclesOptions(updatedOptions);
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  }, [vehicles]);

  const setVehicleData = async () => {
    try {
      if (workShopAppointment?.vehicle_id) {
        setLoader(true);
        const respponse: any = await dispatch(
          getVehicleAlerts({
            vehicle_id: workShopAppointment?.vehicle_id,
            params: { status: 'alert' },
          }),
        ).catch(() => setLoader(false));
        const alertData = respponse?.vehicleAlerts || (Array.isArray(respponse) ? respponse : []);

        const formatedOption = alertData.map((data: any) => ({
          id: data?._id,
          _id: data?._id,
          name: data?.alert_ref ? `${data?.alert_ref} ${data?.title}` : data?.title,
          value: data?._id,
        }));
        const selVehicle: IWorkshopAppointmentVehicleOption | null =
          (vehicles || []).find(data => data?._id === workShopAppointment?.vehicle_id) || null;
        setAlertList([
          ...formatedOption,
          { id: 'OTHER', _id: 'OTHER', name: 'Other', value: 'OTHER' },
        ]);
        setSelectedVehicle(selVehicle || null);
        setWorkshopAppointment({ ...workShopAppointment });

        // Set selected vehicle and notify state
        setSelectedVehicle(selVehicle || null);
        setWorkshopAppointment({ ...workShopAppointment });
        setLoader(false);
      } else {
        // Vehicle deselected — clear slots and reset slot selection
        dispatch(clearAvailableBookingSlots());
        setSelectedSlotId('');
      }
    } catch (error) {
      setLoader(false);
    }
  };

  useEffect(() => {
    setVehicleData();
  }, [users, workShopAppointment?.vehicle_id, vehicles]);

  const currentOrgId = useMemo(() => {
    return selectedOrg?.id || selectedOrg?._id || selectedVehicle?.user_role_data?.organization_id;
  }, [selectedOrg, selectedVehicle]);

  const currentParkingId = useMemo(() => {
    return selectedParking?.id || selectedParking?._id || selectedVehicle?.vehicle_data?.parking_id;
  }, [selectedParking, selectedVehicle]);

  // Re-fetch available slots when the appointment date changes, organization changes, or parking changes
  useEffect(() => {
    if (workShopAppointment?.vehicle_id && currentOrgId && workShopAppointment?.appointment_date) {
      if (isFirstLoad) {
        setIsFirstLoad(false);
      } else {
        setSelectedSlotId('');
        setWorkshopAppointment(prev => ({ ...prev, time: '' }));
      }
      fetchAvailableSlots(currentOrgId, workShopAppointment.appointment_date, currentParkingId);
    }
  }, [
    workShopAppointment?.appointment_date,
    currentOrgId,
    currentParkingId,
    workShopAppointment?.vehicle_id
  ]);


  useEffect(() => {
    try {
      setLoader(true);
      const selectedVehicleData: IWorkshopAppointmentVehicleOption | null =
        vehicles?.find(data => data?._id === workShopAppointment?.vehicle_id) || null;
      setSelectedVehicle(selectedVehicleData);
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  }, [workShopAppointment?.vehicle_id]);
  const handleChange = (
    field: string,
    value: string | IWorkshopAppointmentCustomerData | null | boolean,
  ) => {
    if (field === 'slot') {
      setWorkshopAppointment({
        ...workShopAppointment,
        time: String(value) || '',
      });
    } else {
      setWorkshopAppointment({ ...workShopAppointment, [field]: value });
    }
  };
  const getErrorData = () => {
    return {
      vehicle_id: !workShopAppointment?.vehicle_id?.trim()
        ? t('workshopAppointment.add_edit.error_customer_toast_message')
        : '',
      service_description: !workShopAppointment?.service_description?.trim()
        ? t('workshopAppointment.add_edit.error_service_description')
        : '',
    };
  };
  const isValid = () => {
    if (
      !workShopAppointment?.vehicle_id?.trim() ||
      !workShopAppointment?.service_description?.trim()
    ) {
      const output = getErrorData();
      setError(output);
      return false;
    } else if (
      workShopAppointment?.alert_id === 'OTHER' &&
      !workShopAppointment?.other_service_type?.trim()
    ) {
      setError({
        ...error,
        other_service_type: !workShopAppointment?.other_service_type?.trim()
          ? t('workshopAppointment.add_edit.error_other_service_toast_message')
          : '',
      });
      return false;
    } else {
      const output = getErrorData();
      setError(output);
      return true;
    }
  };
  const uploadMediaForAdd = async () => {
    try {
      const uploadMediaParam =
        files.map(data => {
          return {
            request: {
              file_name: data?.name || '',
              file_type: data?.type || '',
              file_size: data?.size || 0,
            },
            file: data,
          };
        }) || [];
      const mediaResponse: IUploadMediaResponse[] = await Promise.all(
        [...uploadMediaParam].map(data => {
          const params: IUploadImageRequestParam = {
            request: {
              file_name: data?.request?.file_name || '',
              file_type: data?.request?.file_type || '',
              file_size: data?.request?.file_size || 0,
            },
            file: data?.file,
          };
          const res = dispatch(uploadWorkshopMedia({ params }));
          return res;
        }),
      );
      return Promise.resolve(mediaResponse);
    } catch (error) {
      return Promise.reject(error);
    }
  };
  const getRequestData = async () => {
    const uploadMedia = await uploadMediaForAdd().catch(err => {
      return;
    });

    const request: IAddEditWorkshopAppointmentRequest = {
      service_description: workShopAppointment?.service_description || '',
      media: uploadMedia?.length ? uploadMedia.map(data => data?.key || '') : [],
      vehicle_id: workShopAppointment?.vehicle_id || '',
      replace_car: workShopAppointment?.replace_car,
      type: selectedOrganization?.role_name || '',
      user_id: selectedVehicle?.user_data?._id || '',
    };

    if (selectedSlotId) {
      request.slot_id = selectedSlotId;
    } else {
      request.date = moment(workShopAppointment?.appointment_date).format('DD/MM/YYYY') || '';

      const orgId = selectedOrg?.id || selectedOrg?._id;
      if (orgId) {
        request.organization_id = orgId;
      }

      const parkingId = selectedParking?.id || selectedParking?._id;
      if (parkingId) {
        request.parking_id = parkingId;
      }

      if (workShopAppointment?.time) {
        request.time = workShopAppointment.time;
      }
    }

    if (workShopAppointment?.alert_id) {
      if (workShopAppointment.alert_id === 'OTHER') {
        if (workShopAppointment.other_service_type) {
          request.other_service_type = workShopAppointment.other_service_type;
        }
      } else {
        request.alert_id = workShopAppointment.alert_id;
      }
    }

    if (workShopAppointment?.special_note) {
      request.special_note = workShopAppointment.special_note;
    }

    return request;
  };
  const handleCreateWorkshopAppointment = async () => {
    try {
      setLoader(true);
      const request = await getRequestData();
      dispatch(createWorkshopAppointment({ request: { ...request } }))
        .then(() => {
          callBackFun && callBackFun();
          toast.success(
            t('workshopAppointment.add_edit.success_appointment_created_toast_message'),
          );
          dispatch(setSideModalData(initSideModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
    } catch (error) {
      setLoader(false);
    }
  };
  const handleEditWorkshopAppointment = async () => {
    try {
      setLoader(true);

      const payload: any = {
        workshop_id: workshopAppointmentDetails?._id || workShopAppointment?._id || '',
      };

      // 1. replace_car
      if (workShopAppointment?.replace_car !== workshopAppointmentDetails?.replace_car) {
        payload.replace_car = workShopAppointment?.replace_car;
      }


      // 3. slot_id
      const originalSlotId = workshopAppointmentDetails?.slot_id || '';
      if (selectedSlotId !== originalSlotId) {
        payload.slot_id = selectedSlotId || '';
      }

      // 4. media
      const uploadMedia = await uploadMediaForAdd().catch(() => []);
      const newMediaKeys = uploadMedia?.length ? uploadMedia.map(data => data?.key || '') : [];
      const originalMediaKeys = (workshopAppointmentDetails?.media || []).map(data => data?.key || '');
      const currentMediaKeys = [
        ...((workShopAppointment?.media || []).map(data => data?.key || '')),
        ...newMediaKeys
      ];

      const isMediaChanged =
        originalMediaKeys.length !== currentMediaKeys.length ||
        originalMediaKeys.some((k, idx) => k !== currentMediaKeys[idx]);

      if (isMediaChanged) {
        payload.media = currentMediaKeys;
      }

      dispatch(
        editWorkshopAppointment({
          request: payload,
          appointment_id: payload.workshop_id,
        }),
      )
        .then(() => {
          callBackFun && callBackFun();
          toast.success(
            t('workshopAppointment.add_edit.success_appointment_updated_toast_message'),
          );
          dispatch(setSideModalData(initSideModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
    } catch (error) {
      setLoader(false);
    }
  };
  const handleSubmit = () => {
    try {
      if (!selectedOrg) {
        toast.error(t('workshopAppointment.slots.selectOrganisation'));
        return;
      }
      if (!selectedParking) {
        toast.error(t('workshopAppointment.slots.selectParking'));
        return;
      }
      if (!workShopAppointment?.appointment_date?.trim() || !workShopAppointment?.time?.trim()) {
        toast.error(t('workshopAppointment.add_edit.error_appointment_toast_message'));
        return;
      }
      if (!isValid()) {
        return;
      }
      formFor === 'EDIT' ? handleEditWorkshopAppointment() : handleCreateWorkshopAppointment();
    } catch (error) { }
  };
  const handlePreviewFile = (file: File, type: string) => {
    dispatch(
      setPreviewFileData({
        type: type || '',
        content: { url: window.URL.createObjectURL(file) },
      }),
    );
  };
  const getFileContent = (file: File) => {
    const splitedVal = file.type.split('/');
    switch (splitedVal[0]) {
      case 'image':
        return (
          <img
            src={window.URL.createObjectURL(file)}
            className="flex items-center justify-center min-w-full h-full w-12 object-contain"
            alt=""
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          />
        );
      case 'video':
        return (
          <div
            className="flex items-center justify-center bg-grey3 w-12 h-full min-w-full rounded-3 cursor-pointer"
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          >
            <PlayIcon />
          </div>
        );
      default:
        return (
          <div
            className="flex items-center justify-center bg-grey1 w-full h-full rounded-3 cursor-pointer"
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          >
            <SearchIcon />
          </div>
        );
    }
  };
  const getAddedFileContent = (file: IMedia) => {
    const splitedVal = file.type.split('/');
    const handleShowFile = () => {
      dispatch(
        setPreviewFileData({
          type: file.type || '',
          content: { url: file.url },
        }),
      );
    };
    switch (splitedVal[0]) {
      case 'image':
        return (
          <img
            src={file.url}
            className="flex items-center justify-center min-w-full h-full w-12 object-contain"
            alt=""
            onClick={() => handleShowFile()}
          />
        );
      case 'video':
        return (
          <div
            className="flex items-center justify-center bg-grey3 w-12 h-full min-w-full rounded-3 cursor-pointer"
            onClick={() => handleShowFile()}
          >
            <PlayIcon />
          </div>
        );
      default:
        return (
          <div
            className="flex items-center justify-center bg-grey1 w-full h-full rounded-3 cursor-pointer"
            onClick={() => handleShowFile()}
          >
            <SearchIcon />
          </div>
        );
    }
  };
  const handleSetFiles = (file: File) => {
    const updatedFiles = [...files, file];
    setFiles(updatedFiles);
  };
  const handleRemoveFiles = (index: number) => {
    const updatedFiles = (files || []).filter((file, i) => i !== index);
    setFiles(updatedFiles);
  };
  return (
    <CommonLayout
      onConfirm={() => handleSubmit()}
      confirmButtonProps={{ label: t('workshopAppointment.add_edit.schedule') }}
    >
      <div
        className={`flex flex-col w-full h-full p-6 ${selectedOrganization?.role_name === usersRoles?.FLEET_MANAGE ? 'pointer-events-none' : ''
          }`}
      >
        <div className="flex flex-wrap -mx-3">
          <div className="flex w-1/2 pr-1 flex-wrap h-fit">
            <div className="w-full px-3">
              <CloudUpload
                setFile={e => {
                  if (e?.target?.files?.length) {
                    handleSetFiles(e?.target?.files[0]);
                  } else {
                    e.stopPropagation();
                  }
                }}
                acceptedFile={'image/*,video/*'}
                title={t('FILE_TITLE')}

              />
            </div>
            <div className="w-full flex items-center justify-start px-3 mt-3 overflow-hidden">
              <div className="overflow-x-auto w-full max-w-full flex py-2">
                {workShopAppointment?.media?.length ? (
                  workShopAppointment?.media.map((file, index) => (
                    <div
                      className="flex items-center justify-center ml-2 rounded-3 h-12 w-12 max-w-12 min-h-full min-w-fit bg-grey1 relative"
                      key={`${file._id}-${index}`}
                    >
                      <CancelIcon
                        className="flex absolute -top-1 -right-1 cursor-pointer"
                        onClick={() => {
                          const updatedMediaFile = workShopAppointment?.media?.filter(
                            data => data?._id !== file?._id,
                          );
                          setWorkshopAppointment({
                            ...workShopAppointment,
                            media: updatedMediaFile,
                          });
                        }}
                      />
                      {getAddedFileContent(file)}
                    </div>
                  ))
                ) : (
                  <></>
                )}
                {files && files?.length ? (
                  files.map((file, index) => (
                    <div
                      className="flex items-center justify-center ml-2 rounded-3 h-12 w-12 max-w-12 min-h-full min-w-fit bg-grey1 relative"
                      key={`${file?.name}-${index}`}
                    >
                      <CancelIcon
                        className="flex absolute -top-1 -right-1 cursor-pointer"
                        onClick={() => handleRemoveFiles(index)}
                      />
                      {getFileContent(file)}
                    </div>
                  ))
                ) : (
                  <></>
                )}
              </div>
            </div>
            <div className="w-full mt-2 px-3 mb-3">
              <label className="text-base font-bold">
                {t('workshopAppointment.add_edit.title')}
              </label>
            </div>
            {/* Organization Dropdown or Auto-Written Name */}
            {formFor !== 'EDIT' && (
              <div className="w-full px-3">
                <div className="mb-3">
                  <label className="font-medium text-sm text-black2 w-full">
                    {t('workshopAppointment.slots.organisation')}
                  </label>
                  {showOrgDropdown ? (
                    <Select
                      placeholder={t('workshopAppointment.slots.selectOrganisation')}
                      options={orgOptions}
                      selected={selectedOrg}
                      setSelected={setSelectedOrg}
                      isSearchable
                    />
                  ) : (
                    <div className="flex items-center px-4 h-11 rounded-1 border-1.5 border-grey bg-grey8 text-sm font-medium text-black2">
                      {selectedOrg?.name || '-'}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Parking Dropdown or Auto-Written Name */}
            {formFor !== 'EDIT' && (
              <div className="w-full px-3">
                <div className="mb-3">
                  <label className="font-medium text-sm text-black2 w-full">
                    {t('workshopAppointment.slots.parking')}
                  </label>
                  {parkingOptions.length > 1 ? (
                    <Select
                      placeholder={t('workshopAppointment.slots.selectParking')}
                      options={parkingOptions}
                      selected={selectedParking}
                      setSelected={setSelectedParking}
                    />
                  ) : (
                    <div className="flex items-center px-4 h-11 rounded-1 border-1.5 border-grey bg-grey8 text-sm font-medium text-black2">
                      {selectedParking?.name || '-'}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="w-full px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black2 w-full">
                  {t('workshopAppointment.add_edit.vehicle')}
                </label>
                <Select
                  placeholder={t('workshopAppointment.add_edit.placeholder_vehicle')}
                  options={vehiclesOptions.filter(
                    coun =>
                      coun?.value &&
                      coun?.value.toLowerCase().includes(dropdownSearch.toLowerCase()),
                  )}
                  selected={
                    vehiclesOptions?.find(data => data?.id === workShopAppointment?.vehicle_id) ||
                    null
                  }
                  setSelected={data => handleChange('vehicle_id', data?.id)}
                  disabled={!selectedOrg || formFor === 'EDIT'}
                  error={error?.vehicle_id}
                  isSearchable
                  removeSearchOnBlur
                  search={dropdownSearch}
                  setSearch={val => setDropdownSearch(val)}
                />
              </div>
            </div>
            <div className="w-full px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black2 w-full">
                  {t('workshopAppointment.add_edit.alert')}
                </label>
                <Select
                  placeholder={t('workshopAppointment.add_edit.placeholder_alert')}
                  options={alertsList.filter(
                    coun => coun?.name.toLowerCase().includes(alertDropdownSearch.toLowerCase()),
                  )}
                  selected={
                    alertsList?.find(data => data?.id === workShopAppointment?.alert_id) || null
                  }
                  setSelected={data => {
                    const selAlert = selectedVehicleALerts.find(
                      a => a._id === (data?._id || data?.id),
                    );
                    setWorkshopAppointment({
                      ...workShopAppointment,
                      alert_id: data?._id || data?.id,
                      service_description: selAlert ? selAlert?.description : '',
                    });
                  }}
                  disabled={!workShopAppointment?.vehicle_id || formFor === 'EDIT'}
                  isSearchable
                  removeSearchOnBlur
                  search={alertDropdownSearch}
                  setSearch={val => setAlertDropdownSearch(val)}
                />
              </div>
            </div>
            {workShopAppointment?.alert_id === 'OTHER' && (
              <div className="w-full px-3">
                <div className="mb-3">
                  <label className="font-medium text-sm text-black2 w-full">
                    {t('workshopAppointment.add_edit.service_type')}
                  </label>
                  <InputField
                    placeholder={t('workshopAppointment.add_edit.placeholder_service_type')}
                    value={workShopAppointment?.other_service_type || ''}
                    onChange={val => handleChange('other_service_type', val)}
                    error={error?.other_service_type || ''}
                    disabled={formFor === 'EDIT'}
                  />
                </div>
              </div>
            )}
            <div className="w-full px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black2 w-full">
                  {t('workshopAppointment.add_edit.service_description')}
                </label>
                <Textarea
                  placeholder={t('workshopAppointment.add_edit.service_description')}
                  value={workShopAppointment?.service_description}
                  onChange={val => handleChange('service_description', val)}
                  error={error?.service_description || ''}
                  disabled={formFor === 'EDIT'}
                />
              </div>
            </div>
            <div className="w-full px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black2 w-full">
                  {t('workshopAppointment.add_edit.customer')}
                </label>
                <InputField
                  placeholder={t('workshopAppointment.add_edit.placeholder_customer')}
                  value={
                    selectedVehicle?.user_data?.name && selectedVehicle?.user_data?.surname
                      ? `${selectedVehicle?.user_data?.name} ${selectedVehicle?.user_data?.surname}`
                      : ''
                  }
                  onChange={val => { }}
                  error={''}
                  disabled
                />
              </div>
            </div>
            <div className="w-full px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black2 w-full">
                  {t('workshopAppointment.ExpandedUi.customer_email')}
                </label>
                <InputField
                  placeholder={t('workshopAppointment.add_edit.placeholder_email')}
                  value={selectedVehicle?.user_data?.email || ''}
                  onChange={val => { }}
                  error={''}
                  disabled
                />
              </div>
            </div>
            <div className="w-1/2 px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black2 w-full">
                  {t('workshopAppointment.add_edit.phone')}
                </label>
                <InputField
                  placeholder={t('workshopAppointment.add_edit.phone')}
                  value={
                    selectedVehicle?.user_data?.country_code &&
                      selectedVehicle?.user_data?.mobile_no
                      ? `+${selectedVehicle?.user_data?.country_code
                        ? selectedVehicle?.user_data?.country_code.replaceAll('+', '')
                        : ''
                      } ${selectedVehicle?.user_data?.mobile_no || ''}`
                      : ''
                  }
                  onChange={val => { }}
                  error={''}
                  disabled
                />
              </div>
            </div>
            <div className="w-1/2 px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black2 w-full">
                  {t('workshopAppointment.ExpandedUi.vehicle_plate')}
                </label>
                <InputField
                  placeholder={t('workshopAppointment.ExpandedUi.vehicle_plate')}
                  value={selectedVehicle?.plate || ''}
                  onChange={val => { }}
                  error={''}
                  disabled
                />
              </div>
            </div>
            <div className="w-full px-3">
              <div className="mb-3">
                <label className="font-medium text-sm text-black2 w-full">
                  {t('workshopAppointment.add_edit.note')}
                </label>
                <Textarea
                  placeholder={t('workshopAppointment.add_edit.placeholder_note')}
                  value={workShopAppointment?.special_note || ''}
                  onChange={val => handleChange('special_note', val)}
                  disabled={formFor === 'EDIT'}
                />
              </div>
            </div>
            <div className="w-full px-3">
              <div className="flex items-center mb-3 gap-x-2">
                <div className="flex items-center justify-center">
                  <CheckBox
                    checked={workShopAppointment?.replace_car}
                    onChange={checked => handleChange('replace_car', checked)}
                    disabled={selectedOrganization?.role_name === usersRoles.FLEET_MANAGE}
                  />
                </div>
                <label className="text-sm font-medium flex-1">
                  {t('workshopAppointment.add_edit.replace_Car')}
                </label>
              </div>
            </div>
          </div>
          <div className="flex w-1/2 flex-wrap pl-1 h-fit">
            <label className="text-lg font-semibold w-full">
              {t('workshopAppointment.add_edit.appointment_timeslot')}
            </label>
            <div className="w-full flex">
              <DateSelector
                showCurrentMonth={!workShopAppointment?.appointment_date}
                disablePastDays
                startDate={moment.unix(
                  workShopAppointment?.appointment_date
                    ? moment(workShopAppointment?.appointment_date).unix()
                    : moment().unix(),
                )}
                setStartDate={data => handleChange('appointment_date', data)}
              />
            </div>
            {/* Slot picker — populated from /admin/slots after vehicle is selected */}
            <div className="w-full mt-4 px-2">
              {!workShopAppointment?.vehicle_id ? (
                <div className="flex flex-col items-center justify-center py-8 rounded-2 border border-dashed border-grey6 text-grey2 text-sm text-center gap-2">
                  <svg className="w-8 h-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{t('workshopAppointment.slots.selectVehicleFirst') || 'Select a vehicle to see available slots'}</span>
                </div>
              ) : availableBookingSlots.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 rounded-2 border border-dashed border-grey6 text-grey2 text-sm text-center gap-2">
                  <svg className="w-8 h-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{t('workshopAppointment.slots.noSlotsForDate') || 'No available slots for this date'}</span>
                </div>
              ) : (
                <div className="flex flex-col gap-2 max-h-[280px] overflow-y-auto pr-1">
                  {availableBookingSlots.map((slot: ISlot) => {
                    const isSelected = selectedSlotId === slot._id;
                    return (
                      <div
                        key={slot._id}
                        onClick={() => {
                          setSelectedSlotId(slot._id);
                          setWorkshopAppointment(prev => ({
                            ...prev,
                            time: `${slot.start_time} - ${slot.end_time}`,
                            appointment_date: moment(slot.date, ['DD/MM/YYYY', 'YYYY-MM-DD']).toString(),
                          }));
                        }}
                        className={`flex items-center justify-between px-4 py-3 rounded-2 border-2 cursor-pointer transition-all ${isSelected
                            ? 'border-primary bg-primary bg-opacity-5'
                            : 'border-grey6 bg-white hover:border-primary hover:bg-primary hover:bg-opacity-5'
                          }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${isSelected ? 'border-primary' : 'border-grey5'}`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-primary" />}
                          </div>
                          <div>
                            <p className={`text-sm font-bold ${isSelected ? 'text-primary' : 'text-black1'}`}>
                              {slot.start_time} – {slot.end_time}
                            </p>
                          </div>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isSelected ? 'bg-primary text-white' : 'bg-grey8 text-grey2'}`}>
                          {t('workshopAppointment.slots.capacity', { booked: slot.booked_count ?? 0, total: slot.workshop_capacity })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex w-full flex-wrap px-2 mt-4">
              <label className="text-base font-semibold w-full">
                {t('workshopAppointment.add_edit.appointment_date')}
              </label>
              <label className="text-base text-grey9 font-semibold w-full">
                {workShopAppointment?.appointment_date && workShopAppointment?.time
                  ? `${moment(workShopAppointment?.appointment_date).format('DD')} ${t(
                    'months.' + moment(workShopAppointment?.appointment_date).format('MMMM'),
                  )}, ${moment(workShopAppointment?.appointment_date).format('YYYY')} · ${workShopAppointment?.time}`
                  : '-'}
              </label>
            </div>
          </div>
        </div>
      </div>
    </CommonLayout>
  );
};

export default AddEditWorkshopAppointment;
